import { Injectable, Injector, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { BrowserStorageService } from './browser-storage.service';
import { environment } from '../../environments/environment';
import { GeolocalizationService } from './geolocalization.service';
import { EncryptionService } from '../services/encryption.service';
import { TenantIdService } from '@app/core/theme/tenant-id.service';
import { ThemeService } from '@app/core/theme/theme.service';
import { TotpLockService } from '@app/core/services/totp-lock.service';
import { InMemoryCardsSpeiAttemptMemory } from '@app/core/services/cards-spei-attempt-memory.service';
import { InMemorySpeiOrderAttemptMemory } from '@app/core/services/spei-order-attempt-memory.service';
import { MasiveTransferDraftStore } from '@app/transactions/services/masive-transfer-draft.store';
import { InMemoryProductCountsCache } from '@app/core/services/product-counts-cache.service';
import { SessionTpvEntityFocusStore } from '@app/core/services/tpv-entity-focus.store';
import { TpvSelectedMerchantReset } from '@app/domain/ports/tpv/tpv-selected-merchant-reset.port';
import { TAB_MEMORIES } from '@app/core/tab-memory/tab-memory.token';

export interface LogoutRequest {
  refresh: string;
  token_session?: string;
}

export interface LogoutResponse {
  success: boolean;
  message?: string;
}

@Injectable({
  providedIn: 'root'
})
export class LogoutService {

  //private readonly apiUrl = `${environment.apiUrl}/authentication/user/logout/`;
  private readonly apiUrl = `${environment.apiUrl}/api/users/auth/logout/`;
  private encryption;
  private readonly tenantId = inject(TenantIdService);
  private readonly theme = inject(ThemeService);
  private readonly totpLock = inject(TotpLockService);
  // La clase concreta (raíz) y no el puerto: es el MISMO singleton (`useExisting`), y
  // así ningún consumidor de LogoutService necesita el binding del puerto.
  private readonly cardsSpeiAttempts = inject(InMemoryCardsSpeiAttemptMemory);
  // Feature 125: lo mismo para las llaves de SPEI de Suite (clase concreta, mismo singleton).
  private readonly speiOrderAttempts = inject(InMemorySpeiOrderAttemptMemory);
  private readonly masiveDraft = inject(MasiveTransferDraftStore);
  // Feature 152: conteos y listas de directorio TPV de `/products` (clase concreta, mismo singleton).
  private readonly productCounts = inject(InMemoryProductCountsCache);
  // Feature 152: el foco TPV (empresa/unidad) vive en una signal de un singleton raíz;
  // borrar el storage no la vacía, así que se limpia aquí (signal + storage).
  private readonly tpvFocus = inject(SessionTpvEntityFocusStore);
  // Lo de las features (comercio TPV, árbol de accesos de las migas, catálogo de
  // comercios de Payments) se pide AL VACIAR, no al construir: así no se instancian
  // servicios de feature en el login y un spec sin sus enlaces no revienta.
  private readonly injector = inject(Injector);

  constructor(
    private http: HttpClient,
    private browserStorage: BrowserStorageService,
    private geolocalizationService: GeolocalizationService
  ) {
    this.encryption = inject(EncryptionService);
  }

  logout(): Observable<LogoutResponse> {
    // El bloqueo del 2FA era de ESTE usuario. Se apaga ANTES del corte de abajo
    // (sin tokens también es un cierre de sesión) y aquí y no solo en los clear*:
    // el interceptor y el temporizador de inactividad cierran sesión llamando a
    // `logout()` y limpiando el storage a mano, sin pasar por ellos.
    this.totpLock.clear();
    // Features 124/125: las llaves de orden SPEI y el borrador de la masiva eran de
    // ESTE usuario y viven en memoria de la pestaña: el siguiente no puede heredarlos.
    this.forgetTabMemory();
    const encriptedRefreshToken = this.browserStorage.getSessionStorageItem('refresh') || '';
    const token_session = this.browserStorage.getSessionStorageItem('access') || '';

    // Si no hay tokens (ej. login falló con 403, sesión ya limpiada), retornar éxito sin llamar al API
    if (!token_session) {
      return of({ success: true });
    }

    // IMPORTANTE: NO limpiar el storage aquí. Se debe limpiar DESPUÉS de que el API responda.
    // El componente que llama a este servicio debe manejar la limpieza en el operador finalize().

    const accessToken = this.encryption.decrypt(token_session) || '';
    const refreshToken = this.encryption.decrypt(encriptedRefreshToken) || '';
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`
      })
    };

    const logoutData: LogoutRequest = {
      refresh: refreshToken || ''
    };

    // Retornar el observable para que el caller maneje la limpieza después de la respuesta
    return this.http.post<LogoutResponse>(this.apiUrl, logoutData, httpOptions);
  }

  /**
   * Olvida los datos EN MEMORIA de la pestaña que pertenecen al usuario y no son la
   * sesión: los intentos de orden SPEI (Cards, feature 124, y Suite, feature 125) y el
   * borrador de la masiva SPEI (CLABEs, montos, observaciones) y los conteos y listas
   * de directorio de `/products` y el foco TPV (feature 152), y el comercio TPV, el árbol
   * de accesos de las migas de TPV y el catálogo de comercios de Payments
   * (revisión 2026-10-19; los dos últimos por `TAB_MEMORIES`). Lo llaman el cierre de
   * sesión y el INICIO de un login. La próxima memoria de pestaña se añade AQUÍ y la
   * cubren los dos a la vez. El bloqueo del 2FA NO va aquí (decisión del humano
   * 2026-10-07): se vacía solo al cerrar sesión.
   */
  forgetTabMemory(): void {
    this.cardsSpeiAttempts.clear();
    this.speiOrderAttempts.clear();
    this.masiveDraft.clear();
    this.productCounts.clear();
    this.tpvFocus.clear();
    // Revisión 2026-10-19 (B1): el comercio TPV elegido vive en una signal de un
    // singleton raíz; sin esto el siguiente usuario de la pestaña lo heredaba.
    this.injector.get(TpvSelectedMerchantReset, null)?.clear();
    // El árbol de accesos de las migas de TPV y el catálogo de comercios de Payments (Y1).
    for (const memoria of this.injector.get(TAB_MEMORIES, [])) memoria.clear();
  }

  // Método para limpiar el localStorage después del logout exitoso
  clearLocalStorage(): void {
    this.browserStorage.clearSessionStorage();
    // Limpiar también el sessionStorage de selección de empresa
    sessionStorage.removeItem('selectedCompanyId');
    this.limpiarMarca();
  }

  /**
   * Borra la MARCA cacheada. Es lo que evita que se herede entre usuarios del
   * mismo navegador.
   *
   * ⚠️ Estas llaves viven en **localStorage**, y los dos métodos de limpieza de
   * acá llaman a `clearSessionStorage()` — o sea que sobrevivían al cierre de
   * sesión. Encadenado con el anti-FOUC de `index.html`, que pinta los colores
   * cacheados antes de que Angular arranque, el siguiente usuario veía la marca
   * del anterior.
   *
   * `plp_tenant_qa_override` es de sessionStorage y ya se iría con el clear,
   * pero se quita explícitamente para que la intención quede en un solo lugar.
   */
  private limpiarMarca(): void {
    this.totpLock.clear();
    this.forgetTabMemory();
    try {
      localStorage.removeItem('plp_theme_tokens');
      localStorage.removeItem('plp_tenant_logo');
      sessionStorage.removeItem('plp_tenant_qa_override');
    } catch {
      // Storage bloqueado (ventana privada, políticas del navegador): no hay
      // caché que heredar, así que no hay nada que limpiar.
    }

    // ⚠️ Limpiar el almacenamiento NO alcanzaba, y ésta era la fuga real que se
    // veía: cerrar sesión con una marca blanca y entrar con otro usuario dejaba
    // la marca anterior puesta.
    //
    // Son dos cosas que sobreviven al cierre de sesión porque NO viven en el
    // storage:
    //
    //   1. `TenantIdService.resolvedTenantId`, en MEMORIA. Es un singleton de
    //      raíz y el logout navega sin recargar, así que seguía diciendo la
    //      marca anterior — y `resolveExplicit()` lo devuelve ANTES que nada,
    //      así que el login siguiente la re-aplicaba salvo que el usuario nuevo
    //      trajera un `tenant_id` propio y explícito.
    //   2. Las variables CSS ya pintadas en el documento. Aunque la caché se
    //      borre, lo aplicado sigue en pantalla hasta que algo lo reemplace.
    //
    // Se apagan las dos acá: la marca es de la SESIÓN y no puede sobrevivirla.
    try {
      this.tenantId.override(null);
      this.theme.resetToDefaults();
    } catch {
      // Cosmético: si esto fallara, no puede impedir el cierre de sesión.
    }
  }

  // Método para limpiar el sessionStorage después del logout exitoso
  clearSessionStorage(): void {
    this.browserStorage.clearSessionStorage();
    // Asegurar que se limpie la selección de empresa
    sessionStorage.removeItem('selectedCompanyId');
    this.limpiarMarca();
  }
}
